import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Plane, Rocket, Zap, Users, History, Repeat } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import BetControl from "../components/BetControl";
import Confetti from "../components/Confetti";
import CoinRain from "../components/CoinRain";
import { ShareWin } from "../components/WinShareCard";
import { formatCoins } from "../lib/format";
import { playSpin, playWin, playLose, playCrash, playBlip } from "../lib/sound";

const BOT_NAMES = ["R***j", "A***a", "S***h", "P***i", "V***y", "K***n", "M***a", "D***p", "N***a", "G***v", "T***u", "B***y"];

function makeBots() {
  const n = 4 + Math.floor(Math.random() * 4);
  const pool = [...BOT_NAMES].sort(() => Math.random() - 0.5);
  return Array.from({ length: n }, (_, i) => ({
    id: i,
    name: pool[i],
    bet: [50, 100, 200, 250, 500, 1000][Math.floor(Math.random() * 6)],
    target: Math.round((1.2 + Math.random() * 6) * 100) / 100,
    done: false,
    cashed: false,
    at: 0,
  }));
}

export default function Aviator() {
  const { t } = useLang();
  const { user, setCoins } = useAuth();
  const [bet, setBet] = useState(100);
  const [phase, setPhase] = useState("idle");
  const [mult, setMult] = useState(1.0);
  const [result, setResult] = useState(null);
  const [confetti, setConfetti] = useState(0);
  const [rain, setRain] = useState(0);
  const [autoOn, setAutoOn] = useState(false);
  const [autoTarget, setAutoTarget] = useState(2.0);
  const [autoBetOn, setAutoBetOn] = useState(false);
  const [autoBetCount, setAutoBetCount] = useState(5);
  const [autoBetLeft, setAutoBetLeft] = useState(0);
  const [bots, setBots] = useState([]);
  const [feed, setFeed] = useState([]);

  const roundRef = useRef(null);
  const rafRef = useRef(null);
  const cashedRef = useRef(false);
  const startRef = useRef(0);
  const blipRef = useRef(0);
  const autoRef = useRef({ on: false, target: 2.0 });
  const autoBetRef = useRef({ on: false, left: 0 });
  const botsRef = useRef([]);
  const betRef = useRef(100);

  useEffect(() => { autoRef.current = { on: autoOn, target: Number(autoTarget) || 0 }; }, [autoOn, autoTarget]);
  useEffect(() => { autoBetRef.current.on = autoBetOn; }, [autoBetOn]);
  useEffect(() => { betRef.current = bet; }, [bet]);

  const loadFeed = useCallback(() => api.get("/games/aviator/feed").then(({ data }) => setFeed(data)).catch(() => {}), []);
  useEffect(() => { loadFeed(); return () => cancelAnimationFrame(rafRef.current); }, [loadFeed]);

  const maybeContinueAutoBet = () => {
    autoBetRef.current.left = Math.max(0, autoBetRef.current.left - 1);
    setAutoBetLeft(autoBetRef.current.left);
    if (autoBetRef.current.on && autoBetRef.current.left > 0) {
      setTimeout(() => startFlight(), 1600);
    }
  };

  const settle = async (multiplier) => {
    try {
      const { data } = await api.post("/games/aviator/cashout", { round_id: roundRef.current.round_id, multiplier });
      setCoins(data.coins);
      setResult(data);
      if (data.won && data.win > 0) { setConfetti(Date.now()); setRain(Date.now()); playWin(data.win); toast.success(`${t("cashedOut")} ${data.multiplier.toFixed(2)}× · +${formatCoins(data.win)}!`); }
      else { playCrash(); playLose(); toast(`${t("flewAway")} ${data.crash.toFixed(2)}× (-${formatCoins(betRef.current)})`); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPhase("done");
      loadFeed();
      maybeContinueAutoBet();
    }
  };

  const doCashout = (value) => {
    if (cashedRef.current || phase === "done") return;
    cashedRef.current = true;
    cancelAnimationFrame(rafRef.current);
    settle(Number(value.toFixed(2)));
  };

  const tick = () => {
    const elapsed = (performance.now() - startRef.current) / 1000;
    const m = Math.exp(0.12 * elapsed);
    const crash = roundRef.current.crash;

    const now = performance.now();
    if (now - blipRef.current > 280) { blipRef.current = now; playBlip(m); }

    let changed = false;
    for (const b of botsRef.current) {
      if (!b.done && b.target < crash && m >= b.target) { b.done = true; b.cashed = true; b.at = b.target; changed = true; }
    }
    if (m >= crash) {
      for (const b of botsRef.current) if (!b.done) { b.done = true; b.cashed = false; }
      setBots([...botsRef.current]);
      setMult(crash);
      if (!cashedRef.current) { cashedRef.current = true; settle(0); }
      return;
    }
    if (changed) setBots([...botsRef.current]);

    const a = autoRef.current;
    if (a.on && a.target >= 1.01 && m >= a.target && !cashedRef.current) { doCashout(a.target); return; }

    setMult(m);
    rafRef.current = requestAnimationFrame(tick);
  };

  const startFlight = async () => {
    setResult(null);
    setMult(1.0);
    cashedRef.current = false;
    botsRef.current = makeBots();
    setBots([...botsRef.current]);
    playSpin();
    try {
      const { data } = await api.post("/games/aviator/start", { bet: betRef.current });
      roundRef.current = data;
      setCoins(data.coins);
      setPhase("flying");
      startRef.current = performance.now();
      blipRef.current = 0;
      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPhase("idle");
      autoBetRef.current.left = 0;
      setAutoBetLeft(0);
    }
  };

  const start = () => {
    if (phase === "flying") return;
    autoBetRef.current.left = autoBetOn ? Math.max(1, parseInt(autoBetCount) || 1) : 1;
    setAutoBetLeft(autoBetRef.current.left);
    startFlight();
  };

  const crashed = phase === "done" && result && !result.won;
  const flying = phase === "flying";
  const planeY = flying ? -Math.min(120, (mult - 1) * 30) : 0;

  return (
    <div className="space-y-6">
      <Confetti fire={confetti} />
      <CoinRain fire={rain} />
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold gold-text">{t("aviator")}</h1>
        <p className="text-sm text-slate-400 mt-1">{t("aviatorDesc")}</p>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr_300px] gap-5 items-start">
        <div className="mw-card p-4 space-y-4 order-3 lg:order-1" data-testid="aviator-live-panel">
          <div>
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-cyan-300 mb-2">
              <Users size={13} /> {t("thisRound")} {bots.length > 0 && <span className="text-slate-500">· {bots.length} {t("players")}</span>}
            </p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="aviator-players">
              {bots.length === 0 && <p className="text-xs text-slate-600">—</p>}
              {bots.map((b) => (
                <div key={b.id} className="flex items-center justify-between rounded-lg bg-black/30 px-2.5 py-1.5 text-xs">
                  <span className="font-semibold text-slate-300">{b.name}</span>
                  <span className="text-slate-500">{formatCoins(b.bet)}</span>
                  {!b.done ? <span className="text-cyan-400 animate-pulse">{t("flying")}</span>
                    : b.cashed ? <span className="font-mono-num text-emerald-400 font-bold">{b.at.toFixed(2)}× +{formatCoins(Math.round(b.bet * b.at))}</span>
                    : <span className="text-rose-400">{t("busted")}</span>}
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-amber-300 mb-2"><History size={13} /> {t("recentWins")}</p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="aviator-feed">
              {feed.length === 0 && <p className="text-xs text-slate-600">—</p>}
              {feed.map((f, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg bg-black/30 px-2.5 py-1.5 text-xs">
                  <span className="font-semibold text-slate-300">{f.name}</span>
                  {f.won ? <span className="font-mono-num text-emerald-400 font-bold">{f.mult.toFixed(2)}× +{formatCoins(f.win)}</span>
                    : <span className="text-rose-400">{t("busted")}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mw-card p-6 relative overflow-hidden min-h-[320px] flex flex-col items-center justify-center order-1 lg:order-2"
          style={{ background: "radial-gradient(circle at 50% 120%, rgba(157,78,221,0.18), transparent 60%)" }} data-testid="aviator-stage">
          <motion.div animate={flying ? { x: [0, 8, 0], y: [planeY, planeY - 6, planeY] } : {}}
            transition={{ duration: 0.6, repeat: flying ? Infinity : 0 }} className="absolute top-10">
            <Plane size={46} className={crashed ? "text-rose-500 rotate-45" : "text-cyan-300 -rotate-12"} style={{ filter: "drop-shadow(0 0 10px rgba(0,240,255,0.6))" }} />
          </motion.div>
          <div className="font-display text-6xl sm:text-7xl font-extrabold tabular-nums"
            style={{ color: crashed ? "#FF3366" : flying ? "#00F0FF" : "#FFD700", filter: "drop-shadow(0 0 20px currentColor)" }} data-testid="aviator-multiplier">
            {mult.toFixed(2)}×
          </div>
          {crashed && <p className="mt-2 font-display text-xl font-bold text-rose-400" data-testid="aviator-crashed">{t("flewAway")}!</p>}
          {phase === "done" && result?.won && (
            <div className="mt-2 flex flex-col items-center gap-2" data-testid="aviator-won">
              <p className="font-display text-xl font-bold text-emerald-400">{t("cashedOut")} +{formatCoins(result.win)}</p>
              <ShareWin amount={result.win} game="aviator" multiplier={result.multiplier} />
            </div>
          )}
          {phase === "idle" && <p className="mt-2 text-sm text-slate-500">{t("placeBetToFly")}</p>}
          {autoBetLeft > 0 && (flying || phase === "done") && (
            <p className="absolute bottom-3 right-3 text-[11px] text-amber-300/80 flex items-center gap-1"><Repeat size={11} /> {autoBetLeft} {t("roundsWord")}</p>
          )}
        </div>

        <div className="mw-card p-6 space-y-5 order-2 lg:order-3">
          <BetControl bet={bet} setBet={setBet} max={user?.coins} disabled={flying} />

          <div className="rounded-xl border border-amber-500/20 bg-black/30 p-3 space-y-3">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-amber-200"><Zap size={14} /> {t("autoCashOut")}</span>
              <input type="checkbox" checked={autoOn} onChange={(e) => setAutoOn(e.target.checked)} disabled={flying} data-testid="aviator-auto-toggle" className="h-4 w-4 accent-amber-500" />
            </label>
            {autoOn && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-400">{t("at")}</span>
                <input type="number" step="0.1" min="1.01" value={autoTarget} disabled={flying}
                  onChange={(e) => setAutoTarget(e.target.value)} data-testid="aviator-auto-target"
                  className="w-24 rounded-lg border border-amber-500/30 bg-black/40 px-2 py-1.5 font-mono-num focus:outline-none focus:border-amber-500/80" />
                <span className="font-bold text-amber-300">×</span>
              </div>
            )}
            <div className="border-t border-white/10" />
            <label className="flex items-center justify-between cursor-pointer">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-cyan-200"><Repeat size={14} /> {t("autoBet")}</span>
              <input type="checkbox" checked={autoBetOn} onChange={(e) => setAutoBetOn(e.target.checked)} disabled={flying} data-testid="aviator-autobet-toggle" className="h-4 w-4 accent-cyan-500" />
            </label>
            {autoBetOn && (
              <div className="flex items-center gap-2 text-sm">
                <input type="number" min="1" max="50" value={autoBetCount} disabled={flying}
                  onChange={(e) => setAutoBetCount(e.target.value)} data-testid="aviator-autobet-count"
                  className="w-24 rounded-lg border border-cyan-500/30 bg-black/40 px-2 py-1.5 font-mono-num focus:outline-none focus:border-cyan-500/80" />
                <span className="text-slate-400">{t("roundsWord")}</span>
              </div>
            )}
          </div>

          {flying ? (
            <button onClick={() => doCashout(mult)} data-testid="aviator-cashout-btn"
              className="w-full rounded-xl py-4 text-lg font-extrabold bg-emerald-500 text-black hover:bg-emerald-400 transition-colors animate-pulse-glow">
              {t("cashOut")} {mult.toFixed(2)}× · {formatCoins(Math.round(bet * mult))}
            </button>
          ) : (
            <button onClick={start} data-testid="aviator-start-btn" className="w-full btn-gold rounded-xl py-4 text-lg flex items-center justify-center gap-2">
              <Rocket size={18} /> {t("placeBet")}
            </button>
          )}
          <div className="rounded-lg border border-amber-500/20 bg-black/30 py-2 px-3 text-center text-xs text-slate-400">
            {t("aviatorTip")}
          </div>
        </div>
      </div>
    </div>
  );
}
